"use client";
import { sqmToSqft } from "@/app/utils/area";
import { roundToNearest } from "@/app/utils/format-number";
import { Check, Trash } from "@phosphor-icons/react";
import { BBUser, FloorplanConfiguration } from "@/types";
import Link from "next/link";
import React, { useState } from "react";
import Image from "next/legacy/image";

const deleteFloorplan = async (floorplanId: number, projectId: string) => {
  try {
    const response = await fetch(
      `/api/admin/project/${projectId}/configuration/${floorplanId}`,
      {
        method: "DELETE",
      }
    );

    if (!response.ok) {
      throw new Error("Failed to delete floorplan");
    }

    await response.json();
    window.location.reload();
  } catch (error) {
    console.error("Error deleting floorplan:", error);
  }
};

function renderConfig(
  config: FloorplanConfiguration,
  projectId: string,
  currentUrlPath?: string,
  isAdmin?: boolean,
  user?: BBUser
) {
  if (isAdmin) {
    return (
      <div className="flex flex-row items-center gap-2">
        <Link href={`${currentUrlPath}/configurations/${config.id}`}>
          <div className="text-sm opacity-50 hover:underline">
            {roundToNearest(sqmToSqft(config.area), 1)} sq.ft {config.facing}
          </div>
        </Link>
        {config.url && (
          <div className="relative group">
            <Check className="text-green-500 w-4 h-4" />
            <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 hidden group-hover:block bg-gray-800 text-white text-xs rounded-sm py-1 px-2">
              Floorplan selected
            </span>
          </div>
        )}
        <Trash
          className="text-red-500 w-4 h-4 cursor-pointer"
          onClick={() => deleteFloorplan(config.id, projectId)}
        />
      </div>
    );
  }
  if (currentUrlPath) {
    return (
      <div
        key={config.id}
        className="flex items-center justify-between bg-base-100 border border-base-300 rounded-xl px-4 py-3 shadow-sm md:max-w-sm min-w-84"
      >
        <div>
          <div className="text-base font-medium text-gray-500">{config.name}</div>
          <div className="text-lg font-bold mt-1">
            {roundToNearest(sqmToSqft(config.area), 1)}{" "}
            <span className="font-semibold">sq.ft</span>
          </div>
        </div>
        <Link href={`${currentUrlPath}/floorplan/${config.id}`}>
          <button className="btn btn-primary flex items-center gap-2 px-4 text-sm h-9 min-h-0">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.25 12C2.25 12 5.25 5.25 12 5.25s9.75 6.75 9.75 6.75-3 6.75-9.75 6.75S2.25 12 2.25 12z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            View
          </button>
        </Link>
      </div>
    );
  }
  return (
    <div className="text-sm opacity-50">
      {roundToNearest(sqmToSqft(config.area), 1)} sq.ft
    </div>
  );
}

export default function ResidentialConfiguration(args: {
  floorplanConfigurations: any[];
  isAdmin?: boolean;
  projectId?: string;
  currentUrl?: string;
  user?: BBUser;
}) {
  const { floorplanConfigurations, isAdmin, projectId, currentUrl, user } =
    args;

  if (!projectId) {
    return null;
  }
  // Group by BHK type (e.g., 2BHK, 3BHK, 4BHK)
  const groupedByBHK: Record<string, any[]> = {};
  floorplanConfigurations?.forEach((config) => {
    let bhk = "Other";
    if (config.rooms && typeof config.rooms.bedrooms === "number") {
      bhk = `${config.rooms.bedrooms}BHK`;
    } else if (config.name) {
      // Try to extract BHK from the name, e.g., "2BHK", "3 BHK", etc.
      const match = config.name.match(/(\d+)\s*BHK/i);
      if (match) {
        bhk = `${match[1]}BHK`;
      } else {
        bhk = config.bhk || config.bhkType || config.bhk_type || "Other";
      }
    } else {
      bhk = config.bhk || config.bhkType || config.bhk_type || "Other";
    }
    if (!groupedByBHK[bhk]) groupedByBHK[bhk] = [];
    groupedByBHK[bhk].push(config);
  });

  // Sort BHK keys in order: 3BHK, 4BHK, 2BHK, ...
  const bhkOrder = ["3BHK", "4BHK", "2BHK"];
  const bhkKeys = Object.keys(groupedByBHK).sort((a, b) => {
    const ai = bhkOrder.indexOf(a);
    const bi = bhkOrder.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

  return (
    <div className="w-full bg-base-100 w-full md:rounded-xl md:shadow-md ">
      <style>{`
        .bhk-group { display: block; }
        .bhk-group.hidden-bhk { display: none; }
        #show-all-bhk:checked ~ .bhk-groups .bhk-group { display: block; }
        #show-all-bhk:not(:checked) ~ .bhk-groups .bhk-group:nth-child(n+5) { display: none; }
        #show-all-bhk:checked ~ .show-more-btn { display: none; }
        #show-all-bhk:not(:checked) ~ .show-more-btn { display: flex; }
      `}</style>
      <h3 className="md:p-4 flex items-center gap-2 text-xl md:text-lg font-bold">
        Floorplans
      </h3>
      <div className="py-2 md:px-2 relative mb-2">
        {/* Hidden checkbox for toggling */}
        <input type="checkbox" id="show-all-bhk" className="hidden peer" />
        <div className="bhk-groups flex flex-col gap-8">
          {bhkKeys.map((bhk, idx) => (
            <div
              key={bhk}
              className={`bhk-group${idx >= 4 ? " hidden-bhk" : ""}`}
            >
              <h4 className="text-base font-semibold px-2 mb-3">{bhk}</h4>
              <div className="flex flex-col md:flex-row md:flex-wrap gap-3">
                {groupedByBHK[bhk]
                  .sort((a, b) => a.area - b.area)
                  .map((plan) =>
                    renderConfig(plan, projectId, currentUrl, isAdmin, user)
                  )}
              </div>
            </div>
          ))}
        </div>
        {bhkKeys.length > 4 && (
          <label
            htmlFor="show-all-bhk"
            className="show-more-btn mt-6 flex justify-center w-full cursor-pointer"
          >
            <span className="btn btn-outline btn-sm">See all floorplans</span>
          </label>
        )}
      </div>
    </div>
  );
}
